import { createPortal } from "react-dom"
import { ChevronDown, Check } from "lucide-react"
import type { ClientTheme } from "@/app/lib/themes"
import { controlBase, onFocus, onBlur } from "@/app/lib/tableStyles"
import { usePopover } from "@/app/hooks/usePopover"

interface MultiSelectFilterProps {
  /**
   * Checked option values. An empty array is the "All" state: no constraint,
   * every row passes, and the "All" row is ticked instead of any individual one.
   */
  selected: string[]
  onChange: (next: string[]) => void
  /** Shown on the trigger in the "All" state, e.g. "All Operators". */
  placeholder: string
  options: string[]
  width?: string
  theme: ClientTheme
}

/**
 * Themed multi-select matching SelectFilter's look. The list is led by an "All"
 * row that is selected by default. Picking an individual option from the All
 * state selects only that option; further options can then be added. Clearing
 * the last selected option (or ticking "All") returns to the All state, where
 * everything is displayed.
 */
export function MultiSelectFilter({
  selected,
  onChange,
  placeholder,
  options,
  width = "180px",
  theme,
}: MultiSelectFilterProps) {
  const { open, setOpen, triggerRef, popoverRef, popoverStyle } = usePopover<HTMLDivElement>()
  const ctrlBase = controlBase(theme)

  const allMode = selected.length === 0

  const toggle = (opt: string) => {
    // From "All", a click narrows to just that option; otherwise add/remove it.
    // An emptied selection falls back to "All" automatically.
    if (allMode) {
      onChange([opt])
      return
    }
    onChange(selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt])
  }

  const label = allMode
    ? placeholder
    : selected.length <= 2
      ? selected.join(", ")
      : `${selected.length} selected`

  const checkbox = (checked: boolean) => (
    <span
      className="flex items-center justify-center flex-shrink-0"
      style={{
        width: "15px",
        height: "15px",
        borderRadius: "4px",
        border: `1px solid ${checked ? theme.brandPrimary : theme.borderColor}`,
        backgroundColor: checked ? theme.brandPrimary : "transparent",
      }}
    >
      {checked && <Check size={11} style={{ color: theme.textOnBrand }} />}
    </span>
  )

  return (
    <div ref={triggerRef} className="relative flex-shrink-0" style={{ width }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onFocus={onFocus(theme)}
        onBlur={onBlur(theme)}
        style={{
          ...ctrlBase,
          width: "100%",
          display: "flex",
          alignItems: "center",
          paddingLeft: "10px",
          paddingRight: "28px",
          cursor: "pointer",
          textAlign: "left",
          color: allMode ? theme.textMuted : theme.textPrimary,
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      </button>
      <ChevronDown
        size={12}
        style={{
          position: "absolute",
          right: "10px",
          top: "50%",
          transform: "translateY(-50%)",
          color: theme.textDim,
          pointerEvents: "none",
        }}
      />

      {/* Portaled to <body> so the card's overflow-hidden can't clip it. */}
      {open &&
        popoverStyle &&
        createPortal(
          <div
            ref={popoverRef}
            className="rounded-lg overflow-hidden"
            style={{
              ...popoverStyle,
              zIndex: 60,
              overflowY: "auto",
              backgroundColor: theme.bgSurface,
              border: `1px solid ${theme.borderColor}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
          >
            {/* "All" row */}
            <button
              type="button"
              onClick={() => onChange([])}
              className="flex items-center gap-2 w-full px-3 py-2"
              style={{
                background: "none",
                border: "none",
                borderBottom: `1px solid ${theme.borderColor}`,
                cursor: "pointer",
                textAlign: "left",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.bgSurface2
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent"
              }}
            >
              {checkbox(allMode)}
              <span
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: theme.textPrimary,
                }}
              >
                All
              </span>
            </button>

            {options.length === 0 ? (
              <div
                className="px-3 py-3"
                style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textDim }}
              >
                No options
              </div>
            ) : (
              options.map((opt) => {
                const checked = selected.includes(opt)
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => toggle(opt)}
                    className="flex items-center gap-2 w-full px-3 py-2"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.bgSurface2
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent"
                    }}
                  >
                    {checkbox(checked)}
                    <span
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: "12px",
                        color: theme.textPrimary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {opt}
                    </span>
                  </button>
                )
              })
            )}
          </div>,
          document.body
        )}
    </div>
  )
}
